import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { payoutsAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

export default function PayoutsPage() {
  const { organizer } = useAuth();
  const [summary, setSummary] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);

  const load = () => Promise.all([
    payoutsAPI.summary(),
    payoutsAPI.my()
  ]).then(([s, p]) => { setSummary(s.data); setPayouts(p.data); }).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const requestPayout = async () => {
    if (!window.confirm(`Request payout of KSh ${summary.availableBalance.toLocaleString()}?\n\nPlatform fee (${summary.commissionRate}%): KSh ${Math.floor(summary.availableBalance * summary.commissionRate / 100).toLocaleString()}\nYou'll receive: KSh ${summary.availableBalance - Math.floor(summary.availableBalance * summary.commissionRate / 100)}.toLocaleString()}`)) return;
    setRequesting(true);
    try {
      const res = await payoutsAPI.request({});
      toast.success(`Payout request submitted! You'll receive KSh ${res.data.net.toLocaleString()}`);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Request failed'); }
    finally { setRequesting(false); }
  };

  const statusColor = { pending: '#92400E', approved: '#1A2744', rejected: '#DC2626', paid: '#2D7D32' };
  const statusBg = { pending: '#FEF3C7', approved: '#EFF6FF', rejected: '#FEF2F2', paid: '#f0fdf4' };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}>Loading...</div>;

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <div style={{ background: '#1A2744', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <Link to="/organizer/dashboard" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>← Dashboard</Link>
        <span style={{ color: '#fff', fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>Earnings & Payouts</span>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px' }}>
        {/* Summary */}
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 14, marginBottom: 24 }}>
            {[
              { label: 'Gross Revenue', value: `KSh ${summary.totalGross.toLocaleString()}`, icon: '💵' },
              { label: `Platform Fee (${summary.commissionRate}%)`, value: `KSh ${summary.totalCommission.toLocaleString()}`, icon: '🏢', red: true },
              { label: 'Net Earnings', value: `KSh ${summary.totalNet.toLocaleString()}`, icon: '💰' },
              { label: 'Already Paid Out', value: `KSh ${summary.totalPaidOut.toLocaleString()}`, icon: '✅' },
              { label: 'Available Balance', value: `KSh ${summary.availableBalance.toLocaleString()}`, icon: '🏦', highlight: true },
            ].map(s => (
              <div key={s.label} style={{ background: s.highlight ? '#1A2744' : '#fff', borderRadius: 12, padding: 18, textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>{s.icon}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: s.highlight ? '#fff' : s.red ? '#DC2626' : '#1A2744' }}>{s.value}</div>
                <div style={{ fontSize: 11, color: s.highlight ? 'rgba(255,255,255,0.6)' : '#9CA3AF' }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Request Payout */}
        {summary?.availableBalance > 0 && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 24, border: '1px solid #bbf7d0' }}>
            <h3 style={{ margin: '0 0 8px', color: '#1A2744' }}>Request Payout</h3>
            <p style={{ color: '#6B7280', fontSize: 14, margin: '0 0 16px' }}>
              Available: <strong>KSh {summary.availableBalance.toLocaleString()}</strong> · 
              After {summary.commissionRate}% fee you'll receive: <strong style={{ color: '#2D7D32' }}>KSh {(summary.availableBalance - Math.floor(summary.availableBalance * summary.commissionRate / 100)).toLocaleString()}</strong>
            </p>
            <button onClick={requestPayout} disabled={requesting}
              style={{ background: '#2D7D32', color: '#fff', padding: '12px 24px', borderRadius: 8, border: 'none', fontWeight: 700, cursor: requesting ? 'not-allowed' : 'pointer', opacity: requesting ? 0.7 : 1 }}>
              {requesting ? '⏳ Requesting...' : '💸 Request Payout'}
            </button>
          </div>
        )}

        {/* Per-event breakdown */}
        {summary?.events?.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 16px', color: '#1A2744' }}>Revenue by Event</h3>
            {summary.events.map(e => (
              <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#1A2744' }}>{e.name}</div>
                  <div style={{ fontSize: 12, color: '#9CA3AF' }}>{new Date(e.date).toLocaleDateString('en-KE', { dateStyle: 'medium' })} · {e.ticket_sales} tickets</div>
                </div>
                <div style={{ fontWeight: 700, color: '#2D7D32' }}>KSh {e.gross_revenue.toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}

        {/* Payout History */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 24 }}>
          <h3 style={{ margin: '0 0 16px', color: '#1A2744' }}>Payout History</h3>
          {payouts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>No payout requests yet</div>
          ) : payouts.map(p => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #F3F4F6', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 13, color: '#6B7280' }}>{new Date(p.requested_at).toLocaleDateString('en-KE', { dateStyle: 'medium' })}</div>
                <div style={{ fontWeight: 600 }}>KSh {p.net_amount.toLocaleString()} <span style={{ fontWeight: 400, color: '#9CA3AF', fontSize: 12 }}>(gross: KSh {p.amount_requested.toLocaleString()})</span></div>
              </div>
              <span style={{ background: statusBg[p.status], color: statusColor[p.status], padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>
                {p.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
